import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Card, Table, Tag } from 'antd';
import CompanyDashboard from '../../components/general/companyDashboard/companyDashboard';
import { getUsers } from '../../services/users';
import { IUser } from '../../types/interfaces';
import useAuth from '../../hooks/useAuth';
import { Country } from 'country-state-city';
import { USER_TYPES_TEXT } from '../../constants/constants';
import { getPermissionCategory } from '../../constants/permissions';

/**
 * Dashboard mounts the root overview statistics intercepting `userData`.
 * Calculates relative statistics (Limits, User Tables) depending natively on contextual attributes mapped by Go.
 */
const Dashboard: React.FC = () => {
  const { userData, rolePermissions } = useAuth();
  const [users, setUsers] = useState<IUser[]>([]);
  const [loading, setLoading] = useState(false);

  const dynamicMapCount = (rolePermissions || []).filter(p => getPermissionCategory(p) === 'maps' && p !== 'ui:map').length;
  const dynamicReportCount = (rolePermissions || []).filter(p => getPermissionCategory(p) === 'newsletters' && p !== 'ui:newsletter').length;

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await getUsers({ page: 1, limit: 50 });
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  // read-only columns — no "Acciones" column
  const readOnlyColumns = [
    {
      title: 'Tipo',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => {
        const text = USER_TYPES_TEXT[type] || (type ? type.charAt(0).toUpperCase() + type.slice(1) : "—");
        let color = "default";
        if (type === "admin" || type === "super_admin") color = "red";
        else if (type === "empresa") color = "blue";
        else if (type === "gestor") color = "purple";
        else if (type === "empleado") color = "green";

        return (
          <Tag color={color} style={{ borderRadius: "12px", padding: "2px 10px", fontWeight: 500, fontSize: "12px" }}>
            {text}
          </Tag>
        );
      },
    },
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Celular',
      key: 'phone',
      render: (_: any, record: IUser) => {
        if (!record.phone && !record.phonePrefix) return '—';
        let phoneCode = '';
        if (record.phonePrefix) {
          const country = Country.getCountryByCode(record.phonePrefix);
          if (country) {
            phoneCode = `${country.phonecode.includes('+') ? '' : '+'}${country.phonecode}`;
          } else {
            phoneCode = record.phonePrefix;
          }
        }
        return `${phoneCode} ${record.phone || ''}`.trim() || '—';
      },
    },
    {
      title: 'Fecha inicio',
      key: 'startDate',
      render: (_: any, record: any) => {
        const date = record.edges?.belongsToCompany?.[0]?.edges?.subscription?.startDate || record.edges?.subscription?.startDate || record.subscription?.startDate;
        if (!date) return '—';
        const d = dayjs(date);
        if (d.year() <= 1) return '—';
        return d.format('DD/MM/YYYY');
      },
    },
    {
      title: 'Fecha Fin',
      key: 'endDate',
      render: (_: any, record: any) => {
        const date = record.edges?.belongsToCompany?.[0]?.edges?.subscription?.endDate || record.edges?.subscription?.endDate || record.subscription?.endDate;
        if (!date) return '—';
        const d = dayjs(date);
        if (d.year() <= 1) return '—';
        return d.format('DD/MM/YYYY');
      },
    },
    {
      title: 'Días al corte',
      key: 'daysLeft',
      render: (_: any, record: any) => {
        const date = record.edges?.belongsToCompany?.[0]?.edges?.subscription?.endDate || record.edges?.subscription?.endDate || record.subscription?.endDate;
        if (!date) return '—';
        const d = dayjs(date);
        if (d.year() <= 1) return '—';
        const diff = d.diff(dayjs(), 'days');
        if (diff < 0) return 'Expirado';
        return diff;
      },
    },
    {
      title: 'Estado',
      dataIndex: 'active',
      key: 'active',
      render: (active: boolean) => (
        <Tag color={active ? 'green' : 'volcano'}>
          {active ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
    },
    {
      title: 'País',
      dataIndex: 'country',
      key: 'country',
      render: (country: string) => {
        const c = Country.getCountryByCode(country);
        return c ? c.name : country || '—';
      },
    },
  ];

  return (
    <div>
      <h2 style={{ marginBottom: '16px' }}>Panel de control</h2>

      <Card
        title="Información de la organización"
        style={{ marginBottom: 16, borderRadius: '12px' }}
      >
        <CompanyDashboard
          companyName={userData?.name || 'Mi organización'}
          userCount={users.length}
          mapCount={dynamicMapCount}
          reportCount={dynamicReportCount}
        />
      </Card>

      <Card
        title={`Integrantes (${users.length})`}
        style={{ marginBottom: 16, borderRadius: '12px' }}
      >
        <Table
          dataSource={users}
          columns={readOnlyColumns}
          loading={loading}
          rowKey={(record) => record.id}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          size="small"
        />
      </Card>
    </div>
  );
};

export default Dashboard;